import React from "react";
import { Column } from "@/components/Datatable/types";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { Edit, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AdminCatalogProduct } from "../../api/fetchCatalogProducts";

export function createCatalogProductColumns(
  onEdit: (product: AdminCatalogProduct) => void,
  onDelete: (product: AdminCatalogProduct) => void,
  onTogglePublished: (product: AdminCatalogProduct) => void,
  onToggleFeatured: (product: AdminCatalogProduct) => void,
): Column<AdminCatalogProduct>[] {
  return [
    {
      header: "Name",
      accessorKey: "name",
      enableSorting: true,
      cell: ({ row }) => (
        <div>
          <span className="text-gray-900 dark:text-white">
            {row.original.name}
          </span>
          <span className="block text-xs text-gray-500 dark:text-gray-400">
            /{row.original.slug}
          </span>
        </div>
      ),
    },
    {
      header: "Category",
      accessorKey: "category",
      cell: ({ row }) => (
        <span className="inline-flex items-center rounded bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 text-xs text-gray-600 dark:text-gray-300">
          {row.original.category.name}
        </span>
      ),
    },
    {
      header: "Badge",
      accessorKey: "badge",
      cell: ({ row }) => row.original.badge || "--",
    },
    {
      header: "Stages",
      accessorKey: "stages",
      cell: ({ row }) =>
        row.original.stages.length > 0
          ? row.original.stages.map((stage) => stage.name).join(", ")
          : "All",
    },
    {
      header: "Landing Page",
      accessorKey: "landingPageKey",
      cell: ({ row }) => row.original.landingPageKey || "--",
    },
    {
      header: "Featured",
      accessorKey: "featured",
      cell: ({ row }) => (
        <Switch
          checked={row.original.featured}
          onCheckedChange={() => onToggleFeatured(row.original)}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
      ),
    },
    {
      header: "Published",
      accessorKey: "isPublished",
      cell: ({ row }) => (
        <Switch
          checked={row.original.isPublished}
          onCheckedChange={() => onTogglePublished(row.original)}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="h-8 w-8 p-0 hover:bg-gray-200 transition-colors"
              variant="ghost"
            >
              <span className="sr-only">Open menu</span>
              <EllipsisVerticalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[150px] p-2 bg-white">
            <DropdownMenuItem
              className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => onEdit(row.original)}
            >
              <Edit className="mr-2 h-4 w-4" />
              <span>Edit</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center px-3 py-2 text-sm cursor-pointer text-red-600 hover:bg-gray-100 transition-colors"
              onClick={() => onDelete(row.original)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              <span>Delete</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];
}
